import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    FormControlLabel,
    FormLabel,
    styled,
    TextField,
    Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { ChangeEvent, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import RadioButtonGroup from "../../../components/radio-button/radio-button-group";
import { systemDateFormat } from "../../../constants";
import { IRootState } from "../../../redux/root.reducer";
import { hasAccessToHistoricalSnapshots } from "../../../service/user-permission";
import SnapshotAccessDialog from "../../../shared/component/snapshot-access-dialog";
import { IWithClassName, IWithThemeProps } from "../../../theme/default.theme";
import { ToleranceMaxRange } from "../const/tolerance";
import { ListBuilderDownloadAs } from "../enums/download-options";
import { FormParams } from "../enums/form-params";
import { ListBuilderLists } from "../enums/list-options";
import { Steps } from "../enums/steps";
import { TolerancePeriod, ToleranceRange } from "../enums/tolerance";
import { IError, IListDownloadOptionForm } from "../interface/form";
import HistoricalOption from "./historical-option";

const defaultDownloadOptions = [
    {
        id: ListBuilderDownloadAs.SINGLE_FILE,
        name: "A Single File",
    },
    {
        id: ListBuilderDownloadAs.MULTIPLE_FILES_BY_PARENT_LIST,
        name: "Multiple Files, Grouped by Parent Lists and Sublists",
    },
    {
        id: ListBuilderDownloadAs.MULTIPLE_FILES_BY_LIST,
        name: "Separate File for All Lists",
    },
];

const historicalOptionDownloadOptions = [
    {
        id: ListBuilderDownloadAs.SINGLE_FILE,
        name: "Single File for all List(s) and Historical Snapshot(s)",
    },
    {
        id: ListBuilderDownloadAs.MULTIPLE_FILES_BY_PARENT_LIST,
        name: "Multiple Files Grouped by Parent and Sub-list(s) with all Historical Snapshot(s)",
    },
    {
        id: ListBuilderDownloadAs.MULTIPLE_FILES_BY_LIST,
        name: "Multiple Files, One File per List with all Historical Snapshot(s)",
    },
    {
        id: ListBuilderDownloadAs.MULTIPLE_FILES_BY_SNAPSHOT,
        name: "Multiple Files, One File per Snapshot with all List(s)",
    },
    {
        id: ListBuilderDownloadAs.MULTIPLE_FILES_BY_SNAPSHOT_LIST,
        name: "Multiple Files, One File per Snapshot Per List",
    },
];

const listSelectionOptions = [
    { id: ListBuilderLists.ALL, name: "All Lists" },
    {
        id: ListBuilderLists.INCLUDE_CHILDREN,
        name: "The Lists I specify, and their Sublists",
    },
    { id: ListBuilderLists.SELECTED, name: "Only the Lists I specify" },
];

const selector = createSelector(
    (state: IRootState) => state.listBuilder.listBuilder.data,
    (listBuilder) => {
        const {
            [FormParams.LISTS]: lists = ListBuilderLists.ALL,
            [FormParams.DOWNLOAD_AS]:
                downloadAs = ListBuilderDownloadAs.SINGLE_FILE,
            [FormParams.ADD_SNAPSHOTS]: addSnapshots = false,
            [FormParams.INCLUDE_UPDATED_ONLY]: includeUpdatedOnly = false,
            [FormParams.SPLIT_FILES]: splitFiles = false,
            [FormParams.NAME]: name = "",
            [FormParams.SNAPSHOT_DATES]: snapshotDates = [],
            [FormParams.SNAPSHOT_DATE]: snapshotDate,
            [FormParams.TOLERANCE]: tolerance = ToleranceMaxRange[
                TolerancePeriod.MONTHS
            ],
            [FormParams.TOLERANCE_RANGE]: toleranceRange = ToleranceRange.BOTH,
            [FormParams.TOLERANCE_PERIOD]:
                tolerancePeriod = TolerancePeriod.MONTHS,
            [FormParams.SET_FREQUENCY]: setFrequency,
        } = listBuilder ?? {};

        return {
            [FormParams.LISTS]: lists,
            [FormParams.DOWNLOAD_AS]: downloadAs,
            [FormParams.ADD_SNAPSHOTS]: addSnapshots,
            [FormParams.INCLUDE_UPDATED_ONLY]: includeUpdatedOnly,
            [FormParams.SPLIT_FILES]: splitFiles,
            [FormParams.NAME]: name,
            [FormParams.SNAPSHOT_DATES]: snapshotDates.map((date) =>
                dayjs.utc(date).format(systemDateFormat),
            ),
            [FormParams.SNAPSHOT_DATE]: dayjs
                .utc(snapshotDate ?? dayjs())
                .format(systemDateFormat),
            [FormParams.TOLERANCE]: tolerance,
            [FormParams.TOLERANCE_RANGE]: toleranceRange,
            [FormParams.TOLERANCE_PERIOD]: tolerancePeriod,
            [FormParams.SET_FREQUENCY]: !!setFrequency,
        };
    },
);

interface IProps extends IWithClassName {
    onTextChange: (
        step: Steps,
        label: FormParams,
    ) => (event: ChangeEvent<HTMLInputElement>) => void;
    onSelectChange: (
        step: Steps,
        label: FormParams,
    ) => (options: string | Array<string> | number | boolean) => void;
    onCheckBoxChange: (
        step: Steps,
        label: FormParams,
    ) => (event: ChangeEvent<HTMLInputElement>) => void;
    form: IListDownloadOptionForm;
    error: IError;
    initStepForm: (step: Steps, form: IListDownloadOptionForm) => void;
}

const ListDownloadOptions = ({
    className,
    onTextChange,
    onSelectChange,
    form,
    onCheckBoxChange,
    error,
    initStepForm,
}: IProps) => {
    const role = useSelector((state: IRootState) => state.auth.user?.role);
    const hasAccess = hasAccessToHistoricalSnapshots(role);
    const initForm = useSelector(selector);
    const [openDialog, setOpenDialog] = useState(false);

    useEffect(() => {
        initStepForm(Steps.LIST_DOWNLOAD_OPTIONS, initForm);
    }, [initForm]);

    const handleLockClick = () => setOpenDialog(true);
    const handleCloseDialog = () => setOpenDialog(false);
    return (
        <Box className={className}>
            <Card>
                <CardContent>
                    <Box className="list-builder-name-box">
                        <FormLabel className="text-center">
                            Name this List Builder:
                        </FormLabel>
                        <TextField
                            variant="standard"
                            onChange={onTextChange(
                                Steps.LIST_DOWNLOAD_OPTIONS,
                                FormParams.NAME,
                            )}
                            error={!!error[FormParams.NAME]}
                            helperText={error[FormParams.NAME] ?? ""}
                            value={form[FormParams.NAME]}
                        />
                    </Box>
                </CardContent>
            </Card>
            <Box className="option-box">
                <Card className="question-box">
                    <CardHeader title="1. Choose an option to start building a custom list builder download." />
                    <CardContent>
                        <RadioButtonGroup
                            onSelect={onSelectChange(
                                Steps.LIST_DOWNLOAD_OPTIONS,
                                FormParams.LISTS,
                            )}
                            options={listSelectionOptions}
                            value={form[FormParams.LISTS] ?? ""}
                        />
                    </CardContent>
                </Card>
                <Card className="question-box">
                    <CardHeader title="2. Download the List Builder as:" />
                    <CardContent className="builder-q2">
                        {hasAccess ? (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        onChange={onCheckBoxChange(
                                            Steps.LIST_DOWNLOAD_OPTIONS,
                                            FormParams.ADD_SNAPSHOTS,
                                        )}
                                        checked={form[FormParams.ADD_SNAPSHOTS]}
                                    />
                                }
                                label="Add Historical Locations"
                            />
                        ) : (
                            <Typography className="label-text">
                                <img
                                    src="/assets/lock-icon.svg"
                                    className="lock-icon"
                                    alt="No Access"
                                    onClick={handleLockClick}
                                />{" "}
                                <span className="grayed-out">
                                    Add Historical Snapshot(s)
                                </span>
                            </Typography>
                        )}

                        <HistoricalOption
                            tolerance={form[FormParams.TOLERANCE]}
                            tolerancePeriod={form[FormParams.TOLERANCE_PERIOD]}
                            toleranceRange={form[FormParams.TOLERANCE_RANGE]}
                            show={form[FormParams.ADD_SNAPSHOTS]}
                            data={form[FormParams.SNAPSHOT_DATES]}
                            onSelectChange={onSelectChange}
                            snapshotDate={form[FormParams.SNAPSHOT_DATE]}
                            setFrequency={form[FormParams.SET_FREQUENCY]}
                            error={error}
                        />

                        <SnapshotAccessDialog
                            open={openDialog}
                            onClose={handleCloseDialog}
                        />
                        <RadioButtonGroup
                            onSelect={onSelectChange(
                                Steps.LIST_DOWNLOAD_OPTIONS,
                                FormParams.DOWNLOAD_AS,
                            )}
                            options={
                                form[FormParams.ADD_SNAPSHOTS]
                                    ? historicalOptionDownloadOptions
                                    : defaultDownloadOptions
                            }
                            value={form[FormParams.DOWNLOAD_AS] ?? ""}
                        />

                        <Typography className="optional-label">
                            Optional:
                        </Typography>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    onChange={onCheckBoxChange(
                                        Steps.LIST_DOWNLOAD_OPTIONS,
                                        FormParams.SPLIT_FILES,
                                    )}
                                    checked={form[FormParams.SPLIT_FILES]}
                                />
                            }
                            label="Split up Large Files"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    onChange={onCheckBoxChange(
                                        Steps.LIST_DOWNLOAD_OPTIONS,
                                        FormParams.INCLUDE_UPDATED_ONLY,
                                    )}
                                    checked={
                                        form[FormParams.INCLUDE_UPDATED_ONLY]
                                    }
                                />
                            }
                            disabled={form[FormParams.ADD_SNAPSHOTS]}
                            label="Only Include Updated Lists"
                        />
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default styled(ListDownloadOptions)(({ theme }: IWithThemeProps) => ({
    display: "grid",
    gap: 20,
    ".header-box": {
        display: "grid",
        gridTemplateColumns: "1fr 4fr",
        p: {
            color: theme?.palette.announcement.primaryColor,
            fontSize: 22,
        },
    },
    ".text-center": {
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        textAlign: "center",
    },
    ".list-builder-name-box": {
        display: "grid",
        gridTemplateColumns: "160px 4fr",
    },
    ".option-box": {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridGap: 10,
        ".MuiCardHeader-title": {
            fontSize: 16,
            fontWeight: "bold",
        },
        ".MuiCardHeader-root": {
            padding: theme?.spacing(2, 3),
        },
        ".MuiCardContent-root": {
            padding: theme?.spacing(0, 3),
        },
    },
    ".builder-q2": {
        display: "grid",
        ".optional-label": {
            fontWeight: "bold",
            marginTop: theme?.spacing(2),
        },
    },
    ".question-box": {
        padding: theme?.spacing(2),
    },
    ".label-text": {
        display: "flex",
        alignItems: "center",
        marginTop: theme?.spacing(2),
    },
    ".grayed-out": {
        opacity: 0.3,
        cursor: "default",
    },
    ".lock-icon": {
        marginRight: theme?.spacing(2),
        cursor: "pointer",
    },
}));
